/**
 * Ledger Dashboard Page (DRR-109)
 *
 * Resident's ledger account: balance, allocations, recent activity, social tokens.
 * All data from backend GraphQL — ZERO hardcoded field definitions (dumb frontend).
 *
 * Mantine v8.x only. No MUI.
 */

import { useState } from 'react';
import { useTenantId } from '@/auth';
import { useMyActor } from '@/hooks/useMyActor';
// eslint-disable-next-line no-restricted-imports -- DRR-109 ledger queries not yet in codegen

import {
  useGetLedgerAccountQuery,
  useGetLedgerTransfersQuery,
} from '@/generated/graphql';
import {
  IconWallet,
  IconCoin,
  IconHeart,
  IconArrowUpRight,
  IconArrowDownRight,
  IconAlertTriangle,
  IconRefresh,
} from '@tabler/icons-react';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  Table,
  Paper,
  ThemeIcon,
  Alert,
  Button,
  Loader,
  Progress,
  SimpleGrid,
  Tabs,
} from '@mantine/core';
// =========================================================================
// Local types (pending codegen — mirrors backend LedgerTransfer shape)
// =========================================================================

interface LedgerTransferRow {
  transferId: string;
  fromActorId: string;
  toActorId: string;
  amountCents: number;
  tokenType: string;
  socialSubtype?: string;
  serviceCategory?: string;
  status: string;
  createdAt: string;
}

// =========================================================================
// GraphQL Operations (pending codegen — matches backend LedgerQuery)
// =========================================================================

type TabValue = 'overview' | 'activity' | 'social';

// =========================================================================
// Component
// =========================================================================

export function LedgerDashboardPage() {
  const tenantId = useTenantId() ?? '';
  const [activeTab, setActiveTab] = useState<TabValue>('overview');
  const tenantNum = tenantId ? parseInt(tenantId, 10) : 0;

  // Step 1: Get current actor (canonical hook)
  const { actorId: resolvedActorId, loading: actorLoading } = useMyActor();
  const actorId = resolvedActorId ?? '';

  // Step 2: Get ledger account for this actor
  const {
    data: accountData,
    loading: accountLoading,
    error: accountError,
    refetch: refetchAccount,
  } = useGetLedgerAccountQuery({
    variables: { tenantNum, actorId },
    skip: !tenantId || !actorId,
  });

  // Step 3: Get transfers for this tenant
  const {
    data: transfersData,
    loading: transfersLoading,
    error: transfersError,
    refetch: refetchTransfers,
  } = useGetLedgerTransfersQuery({
    variables: { tenantNum },
    skip: !tenantId,
  });

  if (!tenantId) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="No Tenant Selected">
          Please select a tenant to view your ledger.
        </Alert>
      </Container>
    );
  }

  const loading = actorLoading || accountLoading;
  const error = accountError || transfersError;

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Ledger">
          {error.message}
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container size="lg" py="xl">
        <Group justify="center" py="xl">
          <Loader size="lg" />
        </Group>
      </Container>
    );
  }

  const account = (accountData as { ledgerAccount?: { accountId: string; tenantNum: number; actorId: string; balanceCents: number; pointsBalance: number; status: string; createdAt: string } } | undefined)?.ledgerAccount;
  const transfers: LedgerTransferRow[] = (transfersData as { ledgerTransfers?: LedgerTransferRow[] } | undefined)?.ledgerTransfers ?? [];

  const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2)}`;
  const balanceCents = account?.balanceCents ?? 0;
  const pointsBalance = account?.pointsBalance ?? 0;
  const status = account?.status ?? 'inactive';

  // Compute allocation totals from payment transfers grouped by serviceCategory
  const allocationMap = new Map<string, number>();
  for (const t of transfers) {
    if (t.tokenType === 'PAYMENT' && t.serviceCategory) {
      const prev = allocationMap.get(t.serviceCategory) ?? 0;
      allocationMap.set(t.serviceCategory, prev + Math.abs(t.amountCents));
    }
  }
  const allocations = Array.from(allocationMap.entries()).map(([category, amountCents]) => ({
    category,
    amountCents,
  }));
  const totalAllocated = allocations.reduce((s, a) => s + a.amountCents, 0);

  const handleRefresh = () => {
    refetchAccount();
    refetchTransfers();
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>My Ledger</Title>
            <Text c="dimmed" size="sm">
              Account balance, spending, and social tokens
            </Text>
          </div>
          <Group gap="sm">
            <Button
              leftSection={<IconRefresh size={16} />}
              variant="light"
              onClick={handleRefresh}
              size="sm"
            >
              Refresh
            </Button>
            <Badge
              color={status === 'Active' ? 'green' : 'gray'}
              variant="light"
              size="lg"
            >
              {status}
            </Badge>
          </Group>
        </Group>

        {/* Balance Cards */}
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
          <Card withBorder p="lg" radius="md">
            <Group gap="xs">
              <ThemeIcon variant="light" color="blue" size="xl" radius="md">
                <IconWallet size={24} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Balance
                </Text>
                <Text size="xl" fw={700}>
                  {formatCurrency(balanceCents)}
                </Text>
              </div>
            </Group>
          </Card>

          <Card withBorder p="lg" radius="md">
            <Group gap="xs">
              <ThemeIcon variant="light" color="green" size="xl" radius="md">
                <IconCoin size={24} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Allocated
                </Text>
                <Text size="xl" fw={700}>
                  {formatCurrency(totalAllocated)}
                </Text>
              </div>
            </Group>
          </Card>

          <Card withBorder p="lg" radius="md">
            <Group gap="xs">
              <ThemeIcon variant="light" color="pink" size="xl" radius="md">
                <IconHeart size={24} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Social Points
                </Text>
                <Text size="xl" fw={700}>
                  {pointsBalance}
                </Text>
              </div>
            </Group>
          </Card>
        </SimpleGrid>

        {/* Tab Content */}
        <Tabs value={activeTab} onChange={(v) => setActiveTab((v as TabValue) ?? 'overview')}>
          <Tabs.List>
            <Tabs.Tab value="overview">Overview</Tabs.Tab>
            <Tabs.Tab value="activity">Activity</Tabs.Tab>
            <Tabs.Tab value="social">Social Tokens</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="md">
            {/* Allocation Breakdown */}
            <Card withBorder p="md">
              <Text fw={600} mb="md">
                Service Allocations
              </Text>
              {allocations.length === 0 ? (
                <Text ta="center" c="dimmed" py="xl">
                  No spending activity yet
                </Text>
              ) : (
                <Stack gap="sm">
                  {allocations.map((alloc) => (
                    <div key={alloc.category}>
                      <Group justify="space-between" mb={4}>
                        <Text size="sm">{alloc.category}</Text>
                        <Text size="sm" fw={500}>
                          {formatCurrency(alloc.amountCents)}
                        </Text>
                      </Group>
                      <Progress
                        value={
                          totalAllocated > 0
                            ? (alloc.amountCents / totalAllocated) * 100
                            : 0
                        }
                        size="sm"
                        radius="xl"
                      />
                    </div>
                  ))}
                </Stack>
              )}
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="activity" pt="md">
            {/* Transfer History */}
            <Paper withBorder>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Category</Table.Th>
                    <Table.Th>Amount</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {transfersLoading ? (
                    <Table.Tr>
                      <Table.Td colSpan={5}>
                        <Group justify="center" py="md">
                          <Loader size="sm" />
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ) : transfers.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={5}>
                        <Text ta="center" c="dimmed" py="md">
                          No transactions yet
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    transfers.map((t) => (
                      <Table.Tr key={t.transferId}>
                        <Table.Td>
                          {new Date(t.createdAt as string).toLocaleDateString()}
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4}>
                            {(t.amountCents as number) > 0 ? (
                              <IconArrowUpRight size={14} color="green" />
                            ) : (
                              <IconArrowDownRight size={14} color="red" />
                            )}
                            <Text size="sm">{t.tokenType as string}</Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          {(t.serviceCategory as string) ?? (t.socialSubtype as string) ?? '\u2014'}
                        </Table.Td>
                        <Table.Td fw={500}>
                          {formatCurrency(Math.abs(t.amountCents as number))}
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={t.status === 'CONFIRMED' ? 'green' : 'yellow'}
                            size="sm"
                            variant="light"
                          >
                            {t.status as string}
                          </Badge>
                        </Table.Td>
                      </Table.Tr>
                    ))
                  )}
                </Table.Tbody>
              </Table>
            </Paper>
          </Tabs.Panel>

          <Tabs.Panel value="social" pt="md">
            <Card withBorder p="md">
              <Text fw={600} mb="md">
                Social Token Activity
              </Text>
              <Text c="dimmed" size="sm">
                Send and receive recognition tokens with fellow residents.
                Social tokens have zero monetary value — they are community recognition only.
              </Text>
              {transfers.filter((t) => t.tokenType === 'SOCIAL').length === 0 && (
                <Text ta="center" c="dimmed" py="xl">
                  No social token activity yet
                </Text>
              )}
            </Card>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}

export default LedgerDashboardPage;
